/* eslint-disable @atlaskit/platform/no-preconditioning, @atlaskit/platform/one-value-export-per-file -- no-preconditioning: Existing stream processing gates feature behavior from runtime flags. one-value-export-per-file: This file predates the rule and splitting is out of bounds for this change. */
import { fg } from '@atlaskit/platform-feature-flags';
import type { Metric } from '@atlassian/browser-metrics/types';
import { is3pApp } from '@atlassian/conversation-assistant-chat-prompt-input/utils/is-3p-app';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import {
	isFollowUpMessage,
	isStreamActionClassification,
	isStreamActionComplete,
	isStreamActionError,
	isStreamActionPart,
	isStreamActionPrepare,
	isStreamAnswerPart,
	isStreamAnswerGenerationType,
	isStreamAUPError,
	isStreamError,
	isStreamReasoningPart,
	isStreamResponse,
	isStreamTodoSnapshot,
	type TodoItem,
	isStreamTaskStatus,
	isStreamTrace,
	type ParsedSSEMessage,
	type StreamMessage,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { exponentialRetry } from '@atlassian/conversation-assistant-service/common/utils/exponential-retry';
import {
	CHAT_STORE_STATUS,
	type ChatStorePrivateState,
	type Conversation,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { reconnectionMetrics } from '../../common/utils/browser-metrics';
import { randomIntFromInterval } from '../../common/utils/id-generator';

import { NoFinalResponseError } from './error-classes/NoFinalResponseError';
import { StreamErrorException } from './error-classes/StreamErrorException';
import { StreamGeneratorError } from './error-classes/StreamGeneratorError';
import { interactionModeRequestedAttributes } from './interaction-mode';
import { createReasoningChunkBuffer, type ReasoningChunkBuffer } from './reasoning-chunk-buffer';
import { createReconnectableStream } from './reconnectable-stream';
import { withReplayFlush } from './replay-flush';
import { processResearchStreamMessage } from './send-message-action';
import { createStoreAbortController } from './store-abort-controller';
import type { ChatStoreAction, ChatStoreContainerProps, ResearchStreamState } from './types';
import { clearTracesIfNotEmpty } from './utils';
import {
	addActionPart,
	addAnswerPart,
	updateStoreAssistantMessageAnswerGenerationType,
	addAnswerPartWithMarkdownDataMap,
	clearStatus,
	createStreamMarkdownMultiTagProcessor,
	extractStreamMeta,
	findStoreMessageIndex,
	getAssociatedHumanMessageId,
	getLastAgentMessageID,
	isClientSideStreamingError,
	markStreamingMessagesAsComplete,
	processStreamResponseMarkdown,
	replaceAnswerData,
	shouldPreprocessDetailsForConversation,
	updateHumanMessageId,
	type StreamMeta,
} from './utils';

interface ReconnectStreamMessageParams {
	generator: AsyncGenerator<ParsedSSEMessage<StreamMessage>>;
	messageId: string;
	answerIndex: number;
	conversationId: string;
	traceId: string | undefined;
	isStagingArea: boolean;
	isDeepResearch?: boolean;
	getState: () => ChatStorePrivateState;
	setMessages: ChatStoreContainerProps['setMessages'];
	getMessages: ChatStoreContainerProps['getMessages'];
	updateCurrentConversation: (data: Partial<Conversation>) => void;
	addImplicitResearchPlan: ChatStoreContainerProps['addImplicitResearchPlan'];
	resetHasReceivedAnswerPart: ChatStoreContainerProps['resetHasReceivedAnswerPart'];
	processStreamResearch: ChatStoreContainerProps['processStreamResearch'];
	processMessageStreamAction?: ChatStoreContainerProps['processMessageStreamAction'];
	processMessageStreamComplete?: ChatStoreContainerProps['processMessageStreamComplete'];
	processMessageStreamActionClassification?: ChatStoreContainerProps['processMessageStreamActionClassification'];
	sendAnalyticsEvent: ChatStoreContainerProps['sendAnalyticsEvent'];
	onStreamEvent: (
		event: 'error' | 'chunk',
		streamMessage?: StreamMessage,
		eventId?: string,
	) => void;

	/** True while replaying historical events. Wired from withReplayFlush. */
	getIsReplaying?: () => boolean;

	/** When provided, REASONING_PART replay chunks are buffered here instead of dispatched per event. */
	reasoningChunkBuffer?: ReasoningChunkBuffer;

	/** Called after the generator exhausts (success path). Use to drain caller-side buffers. */
	onStreamEnd?: () => void;

	onTaskStatusDraft?: (args: {
		taskId: string;
		title: string;
		updatedAt?: string;
		messageId?: string;
	}) => void;

	onTaskStatusCompleted?: (args: {
		title: string;
		updatedAt?: string;
		completionMessageId?: string;
	}) => void;

	/** Called when a TODO_SNAPSHOT event is received. Mirror of send-message-action's hook. */
	onTodoSnapshot?: (args: { todos: TodoItem[] }) => void;
}
export const processReconnectStreamMessage = async ({
	generator,
	messageId,
	conversationId,
	isDeepResearch,
	isStagingArea,
	answerIndex,
	traceId,
	getMessages,
	setMessages,
	processStreamResearch,
	getState,
	updateCurrentConversation,
	sendAnalyticsEvent,
	onStreamEvent,
	getIsReplaying,
	reasoningChunkBuffer,
	addImplicitResearchPlan,
	resetHasReceivedAnswerPart,
	onStreamEnd,
	onTaskStatusDraft,
	onTaskStatusCompleted,
	onTodoSnapshot,
	processMessageStreamAction,
	processMessageStreamComplete,
	processMessageStreamActionClassification,
}: ReconnectStreamMessageParams): Promise<{
	hasReceivedFinalResponse: boolean;
}> => {
	// Gate the details-tag preprocessor to 3P-agent conversations only.
	const shouldPreprocessDetails = shouldPreprocessDetailsForConversation(
		getState(),
		conversationId,
	);
	const processStreamMarkdownMultiTag = createStreamMarkdownMultiTagProcessor({
		shouldPreprocessDetails,
	});
	let latestStreamMessage: StreamMessage | undefined;
	let hasReceivedFinalResponse = false;

	// Research store state shared across stream messages
	const researchState: ResearchStreamState = {
		clientRoundCounter: 0,
		hasAccumulatedAnswerContent: false,
	};

	let requestId: string | undefined;

	try {
		for await (const streamObj of generator!) {
			const streamMessage = streamObj.data;
			latestStreamMessage = streamMessage;
			onStreamEvent('chunk', streamMessage, streamObj.id);

			// TRACE events are not handled in reconnect; drop early to skip
			// the remaining type-checks below.
			if (isStreamTrace(streamMessage)) {
				continue;
			}

			// This is the first moment we have the network request id for E2E event tracking
			// Store requestId on human message for E2E event tracking
			if (!requestId && 'metadata' in streamMessage) {
				requestId = streamMessage.metadata?.request_id ?? undefined;
			}

			// Buffer REASONING_PART during replay; bufferEvent returns false for
			// orphan chunks (no stepId) so they fall through.
			if (
				reasoningChunkBuffer &&
				getIsReplaying?.() &&
				isStreamReasoningPart(streamMessage) &&
				reasoningChunkBuffer.bufferEvent(streamMessage)
			) {
				continue;
			}

			/**
			 * Research store processing — handles think-deeper traces, implicit round
			 * detection, and research plan updates for all message types.
			 */
			processResearchStreamMessage({
				streamMessage,
				tmpAssistantMessageId: messageId,
				conversationId,
				deepResearchEnabled: isDeepResearch ?? false,
				getMessages,
				setMessages,
				processStreamResearch,
				addImplicitResearchPlan,
				resetHasReceivedAnswerPart,
				researchState,
			});

			/**
			 * Task status processing
			 */
			if (isStreamTaskStatus(streamMessage)) {
				const taskMessage = streamMessage.message;
				if (taskMessage.status === 'DRAFT') {
					onTaskStatusDraft?.({
						taskId: taskMessage.task_id,
						title: taskMessage.title,
						updatedAt: taskMessage.updated_at,
						messageId,
					});
				} else if (taskMessage.status === 'COMPLETED') {
					onTaskStatusCompleted?.({
						title: taskMessage.title,
						updatedAt: taskMessage.updated_at,
						completionMessageId: messageId,
					});
				}
			}

			/**
			 *  snapshot processing
			 */
			if (isStreamTodoSnapshot(streamMessage)) {
				onTodoSnapshot?.({ todos: streamMessage.message.todos });
			}

			/**
			 *  ANSWER_PART  processing
			 */
			if (isStreamAnswerPart(streamMessage)) {
				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
				let processedStreamMessage = streamMessage;
				let hasMarkdownData = false;

				const result = processStreamMarkdownMultiTag(processedStreamMessage);
				processedStreamMessage = result.processedStreamMessage;
				if (Object.keys(result.markdownData).length > 0) {
					hasMarkdownData = true;
					setMessages(
						conversationId,
						addAnswerPartWithMarkdownDataMap({
							chat: getMessages(conversationId),
							answerId: messageId,
							answerPart: processedStreamMessage,
							markdownData: result.markdownData,
							useIndex: answerIndex,
						}),
					);
				}
				if (!hasMarkdownData) {
					setMessages(
						conversationId,
						addAnswerPart({
							chat: getMessages(conversationId),
							answerId: messageId,
							answerPart: streamMessage,
							useIndex: answerIndex,
						}),
					);
				}
			}

			if (isStreamAnswerGenerationType(streamMessage) && fg('rovo_chat_enable_lumina')) {
				setMessages(
					conversationId,
					updateStoreAssistantMessageAnswerGenerationType(
						getMessages(conversationId),
						messageId,
						streamMessage.message.answer_generation_type,
					),
				);
			}

			if (
				(isStreamActionPart(streamMessage) || isStreamActionPrepare(streamMessage)) &&
				fg('aifc_create_enabled')
			) {
				const updatedMessages = addActionPart({
					chat: getMessages(conversationId),
					messageId,
					actionPart: streamMessage,
					useIndex: answerIndex,
				});

				if (isStreamActionPrepare(streamMessage)) {
					const actionMessage = streamMessage.message;
					setMessages(conversationId, [
						...updatedMessages,
						{ type: 'ACTION_PREPARE', action: actionMessage },
					]);
				} else {
					setMessages(conversationId, updatedMessages);
				}

				// Gated behind aifc_fix_reconnect_stream_overlap so rollback is safe.
				if (fg('aifc_fix_reconnect_stream_overlap')) {
					if (!getIsReplaying?.()) {
						processMessageStreamAction?.(messageId, streamMessage, traceId, requestId, undefined, {
							singleInstrumentationId: messageId,
						});
					} else {
						sendAnalyticsEvent({
							type: 'sendOperationalEvent',
							data: {
								action: 'suppressed',
								actionSubject: 'streamActionPart',
								actionSubjectId: 'reconnectReplay',
								source: 'reconnectToInProgressStreamProvider',
								attributes: {
									messageId,
									conversationId,
									reason: 'replay',
									key: isStreamActionPart(streamMessage) ? streamMessage.message?.key : 'prepare',
								},
							},
						});
					}
				} else {
					processMessageStreamAction?.(messageId, streamMessage, traceId, requestId, undefined, {
						singleInstrumentationId: messageId,
					});
				}
			}

			if (isStreamActionComplete(streamMessage) && fg('aifc_create_enabled')) {
				const actionMessage = streamMessage.message;
				setMessages(conversationId, [
					...getMessages(conversationId),
					{ type: 'ACTION_COMPLETE', action: actionMessage },
				]);
				// Gated behind aifc_fix_reconnect_stream_overlap so rollback is safe.
				if (fg('aifc_fix_reconnect_stream_overlap')) {
					if (!getIsReplaying?.()) {
						processMessageStreamComplete?.(messageId, streamMessage, traceId, requestId, {
							singleInstrumentationId: messageId,
						});
					} else {
						sendAnalyticsEvent({
							type: 'sendOperationalEvent',
							data: {
								action: 'suppressed',
								actionSubject: 'streamActionComplete',
								actionSubjectId: 'reconnectReplay',
								source: 'reconnectToInProgressStreamProvider',
								attributes: {
									messageId,
									conversationId,
									reason: 'replay',
									key: actionMessage?.key,
								},
							},
						});
					}
				} else {
					processMessageStreamComplete?.(messageId, streamMessage, traceId, requestId, {
						singleInstrumentationId: messageId,
					});
				}
			}

			if (isStreamActionClassification(streamMessage) && fg('aifc_create_enabled')) {
				if (fg('platform_editor_ai_promptversion_in_analytics')) {
					// Only call during live phase. If ACTION_CLASSIFICATION arrives during
					// replay it means the editor already received it before disconnect.
					if (!getIsReplaying?.()) {
						processMessageStreamActionClassification?.(
							messageId,
							streamMessage,
							traceId,
							requestId,
							{ singleInstrumentationId: messageId },
						);
					}
				}
			}

			/**
			 * FINAL_RESPONSE processing
			 */
			if (isStreamResponse(streamMessage)) {
				hasReceivedFinalResponse = true;
				const humanMessageId = streamMessage?.message?.human_message?.id.toString();
				const { processedStreamMessage, markdownData } = processStreamResponseMarkdown({
					streamMessage,
					messageId,
					conversationId,
					sendAnalyticsEvent,
					shouldPreprocessDetails,
				});

				setMessages(
					conversationId,
					replaceAnswerData({
						chat: getMessages(conversationId),
						answerId: messageId,
						content: processedStreamMessage,
						useIndex: answerIndex,
						humanMessageId,
						markdownData: Object.keys(markdownData).length > 0 ? markdownData : undefined,
					}),
				);

				// Update the StoreHuman entry's ID from the temporary client-generated ID
				// to the real server-assigned ID. After a page reload this is a no-op because
				// the store already contains the real ID.
				if (humanMessageId) {
					const currentHumanMessageId = getAssociatedHumanMessageId(
						getMessages(conversationId),
						messageId,
					);
					if (currentHumanMessageId && currentHumanMessageId !== humanMessageId) {
						setMessages(
							conversationId,
							updateHumanMessageId({
								chat: getMessages(conversationId),
								oldId: currentHumanMessageId,
								newId: humanMessageId,
							}),
						);
					}
				}
			}

			/**
			 * Follow up processing
			 */
			if (isFollowUpMessage(streamMessage)) {
				const messages = getMessages(conversationId);
				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

				setMessages(conversationId, [
					...messages,
					{
						type: 'FOLLOW_UP',
						content: streamMessage,
						id: randomIntFromInterval(0, 1000000),
						agentMessageId: getLastAgentMessageID(messages),
					},
				]);
			}

			/**
			 * Action error processing
			 */
			if (isStreamActionError(streamMessage)) {
				const actionError = new StreamErrorException(
					streamMessage.message.data?.message || 'Action execution failed',
					{
						detail: {
							message: {
								content: streamMessage?.message?.data?.message || 'Action execution failed',
								message_template: streamMessage?.message?.key,
							},
							metadata: {
								...streamMessage.metadata,
								isStagingArea,
							},
						},
						traceId,
					},
				);
				throw actionError;
			}

			if (isStreamError(streamMessage)) {
				onStreamEvent('error', streamMessage, streamObj.id);
				const { message, metadata, millisOffset, type } = streamMessage;

				throw new StreamErrorException(message?.content || 'Unknown', {
					detail: {
						message,
						metadata,
						millisOffset,
						type,
					},
					traceId,
				});
			}
		}
		// Drain any remaining buffered chunks and clear the periodic timer.
		//cancelReasoningFlush();
		onStreamEnd?.();
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		if (error instanceof StreamErrorException) {
			throw error;
		}

		throw new StreamGeneratorError(
			error.message ?? 'Stream generator error',
			{
				traceId,
				detail: {
					millisOffset:
						latestStreamMessage && 'millisOffset' in latestStreamMessage
							? latestStreamMessage.millisOffset
							: undefined,
					type: latestStreamMessage?.type ?? '',
				},
			},
			error,
		);
	}

	return { hasReceivedFinalResponse };
};

export const createReconnectionMetrics = (params: {
	isDeepResearchEnabled?: boolean;
	thinkDeeperEnabled?: boolean;
	fastModeEnabled?: boolean;
}): {
	timeToConnectToStreamMetrics: Metric;
	timeToResumeStreamMetrics: Metric;
} => {
	const timeToConnectToStreamMetrics = reconnectionMetrics.createTimeToConnectToStreamMetrics({
		deepResearchEnabled: params.isDeepResearchEnabled,
		thinkDeeperEnabled: params.thinkDeeperEnabled,
		fastModeEnabled: params.fastModeEnabled,
	});

	const timeToResumeStreamMetrics = reconnectionMetrics.createTimeToResumeStreamMetrics({
		deepResearchEnabled: params.isDeepResearchEnabled,
		thinkDeeperEnabled: params.thinkDeeperEnabled,
		fastModeEnabled: params.fastModeEnabled,
	});

	return {
		timeToConnectToStreamMetrics,
		timeToResumeStreamMetrics,
	};
};

const MAX_RECONNECT_ATTEMPTS = 3;
const RETRY_BASE_DELAY_MS = 1000;
const POST_ONLINE_DELAY_MS = 2000;
const RECONNECT_ABORT_REASON_CHAT_UNMOUNTED = 'chat-unmounted';

/**
 * Determines if an error from the reconnection stream is retryable.
 * Only client-side/network errors are retried. Server-side errors (AUP, stream errors),
 * HTTP errors, and user aborts are not.
 */
export const isRetryableReconnectionError = (error: unknown): boolean => {
	if (error instanceof Error && error.name === 'AbortError') {
		return false;
	}

	// StreamGeneratorError wraps the original error as `cause` — check the underlying error
	if (error instanceof StreamGeneratorError) {
		return isClientSideStreamingError(error.cause ?? error);
	}

	return isClientSideStreamingError(error);
};

export type ChatStoreActionsForReconnect = {
	setConversationData: (params: {
		conversationId: string;
		data: Partial<Conversation>;
	}) => ChatStoreAction;
};

export type ReconnectCompletionResult = {
	success: boolean;
	error?: Error;
	streamMeta?: StreamMeta;
};

export type ReconnectToInProgressStreamParams = {
	conversationId: string;
	messageId: string;
	lastEventId?: string;
	answerIndex?: number;
	isDeepResearch?: boolean;
	abortCurrentRequest?: boolean;
	onComplete?: (result: ReconnectCompletionResult) => void;
	isAutoReconnect?: boolean;
	isReconnectToAll?: boolean;
};

export const reconnectToInProgressStreamProvider: (
	params: ReconnectToInProgressStreamParams & ChatStoreActionsForReconnect,
) => ChatStoreAction =
	({
		conversationId,
		messageId,
		answerIndex: _answerIndex,
		isDeepResearch,
		lastEventId,
		setConversationData,
		abortCurrentRequest,
		onComplete,
		isAutoReconnect,
		isReconnectToAll,
	}) =>
	async (
		{ getState, dispatch },
		{
			assistanceService,
			experienceTracker,
			sendAnalyticsEvent,
			trackAIMAUEvent,
			getMessages,
			setMessages,
			processStreamResearch,
			addImplicitResearchPlan,
			resetHasReceivedAnswerPart,
			chatContextStoreActions,
			markIncompleteResearchesErrored,
			endReconnection,
			startReconnection,
			isReconnecting,
			getIsChatMounted,
			processMessageStreamAction,
			processMessageStreamComplete,
			processMessageStreamActionClassification,
			unifiedTasksActions,
		},
	) => {
		const currentConversation = getState().conversations[conversationId];

		if (abortCurrentRequest && currentConversation?.storeAbortController) {
			currentConversation.storeAbortController.abort();
		}

		// Skip reconnection if there is another operation in progress
		if (
			(currentConversation?.storeAbortController && !abortCurrentRequest) ||
			isReconnecting(conversationId)
		) {
			return;
		}

		startReconnection(conversationId, messageId);

		const reconnectionPerformanceMetrics = createReconnectionMetrics({
			isDeepResearchEnabled: isDeepResearch,
			thinkDeeperEnabled: false,
			fastModeEnabled: false,
		});

		let totalAttempts = 0;
		const reconnectStartedAt = new Date().toISOString();
		// TODO not complete information, ideally the message in progress should indicate what was the requested mode even before message is completed
		const requestedModeAttributes = interactionModeRequestedAttributes(
			isDeepResearch ? 'deep_research' : 'auto',
		);

		const metrics = {
			// Reconnection metrics
			onReconnectStarted: () => {
				reconnectionPerformanceMetrics.timeToConnectToStreamMetrics.start();
				experienceTracker.start({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					id: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					attributes: {
						conversationId,
						messageId,
						isDeepResearch,
						isAutoReconnect,
					},
				});
				// Only fire MAU events for page-load auto-reconnects, not send-message reconnects.
				// Todo isAutoReconnect check should be removed after reconnect-all experiment roll out,
				// reconnect-action will be only triggered on page load.
				if (isAutoReconnect) {
					trackAIMAUEvent({
						type: 'initiated',
						userGenerated: true,
						proactive: false,
						triggeredByAutoReconnect: true,
						singleInstrumentationID: messageId,
						deepResearchEnabled: isDeepResearch ?? false,
						timestamp: reconnectStartedAt,
						attributes: requestedModeAttributes,
					});
				}
			},
			// Was able to connect to active stream
			onReconnectEstablished: () => {
				reconnectionPerformanceMetrics.timeToConnectToStreamMetrics.stop();
				reconnectionPerformanceMetrics.timeToResumeStreamMetrics.start();
			},
			onReconnectFailed: (error: Error) => {
				reconnectionPerformanceMetrics.timeToConnectToStreamMetrics.cancel();
				reconnectionPerformanceMetrics.timeToResumeStreamMetrics.cancel();
				experienceTracker.fail({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					error: error,
					attributes: {
						conversationId,
						messageId,
						isDeepResearch,
						totalAttempts,
					},
				});
				if (isAutoReconnect) {
					trackAIMAUEvent({
						type: 'error',
						userGenerated: true,
						proactive: false,
						triggeredByAutoReconnect: true,
						messageId,
						singleInstrumentationID: messageId,
						deepResearchEnabled: isDeepResearch ?? false,
						timestamp: new Date().toISOString(),
						attributes: requestedModeAttributes,
					});
				}
			},
			onReconnectChunkReceived: () => {
				reconnectionPerformanceMetrics.timeToResumeStreamMetrics.stop();
			},
			onReconnectAborted: () => {
				experienceTracker.abort({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					reason: 'Aborted',
				});
				reconnectionPerformanceMetrics.timeToResumeStreamMetrics.cancel();
				reconnectionPerformanceMetrics.timeToConnectToStreamMetrics.cancel();
				// Todo isAutoReconnect check should be removed after reconnect-all experiment roll out,
				if (isAutoReconnect) {
					trackAIMAUEvent({
						type: 'dismissed',
						userGenerated: true,
						proactive: false,
						triggeredByAutoReconnect: true,
						messageId,
						singleInstrumentationID: messageId,
						deepResearchEnabled: isDeepResearch ?? false,
						timestamp: new Date().toISOString(),
						attributes: requestedModeAttributes,
					});
				}
			},
			onReconnectSucceeded: () => {
				experienceTracker.succeed({
					name: ExperienceName.RECONNECT_TO_ACTIVE_STREAM,
					attributes: {
						totalAttempts,
						conversationId,
						messageId,
						isDeepResearch,
						isAutoReconnect,
					},
				});
				if (isAutoReconnect) {
					trackAIMAUEvent({
						type: 'viewed',
						userGenerated: true,
						proactive: false,
						triggeredByAutoReconnect: true,
						messageId,
						singleInstrumentationID: messageId,
						deepResearchEnabled: isDeepResearch ?? false,
						citedProductList: streamMeta?.citedProductList ?? {},
						sourceProducts: streamMeta?.sourceProducts ?? {},
						pluginsInvoked: streamMeta?.pluginsInvoked,
						skillsInvoked: streamMeta?.skillsInvoked,
						executedStratusSkill: streamMeta?.executedStratusSkill,
						responseSources: streamMeta?.responseSources,
						usesJsm: streamMeta?.usesJsm,
						messageActions: streamMeta?.messageActions,
						conversationChannelId: conversationId,
						timestamp: new Date().toISOString(),
						attributes: {
							...(streamMeta?.interactionModeExecuted
								? { interactionModeExecuted: streamMeta.interactionModeExecuted }
								: {}),
						},
					});
					// Fire agentMessage received for funnel parity with processStream.
					sendAnalyticsEvent({
						eventType: 'track',
						action: 'received',
						actionSubject: 'agentMessage',
						attributes: {
							messageId,
							humanMessageId: streamMeta?.humanMessageId,
							pluginsInvoked: streamMeta?.pluginsInvoked ?? [],
							citedProductList: streamMeta?.citedProductList,
							sourceProducts: streamMeta?.sourceProducts,
							has3pApps: Object.keys(streamMeta?.sourceProducts ?? {}).some(is3pApp),
							timestamp: new Date().toISOString(),
							messageActions: streamMeta?.messageActions,
							deepResearchEnabled: isDeepResearch ?? false,
							initiator: 'rovoChat',
							deepResearchInvoked: isDeepResearch ?? false,
							scenarioId: streamMeta?.scenarioId,
							isReconnect: true,
						},
					});
				}
			},
		};

		const updateCurrentConversation = (data: Partial<Conversation>) => {
			dispatch(
				setConversationData({
					conversationId,
					data,
				}),
			);
		};

		const cancelMessageStream = () => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			assistanceService.cancelMessageStream({ conversationId, messageId }).catch((err: any) => {
				logException({
					name: 'rovoCancelMessageStream',
					exception: err,
					tags: { conversationId, messageId },
				});
			});
		};

		const messages = getMessages(conversationId);
		const answerIndex = _answerIndex ?? findStoreMessageIndex(messages, messageId);
		const { abortController, storeAbortController, confirmAbort } = createStoreAbortController();
		let traceId: string | undefined;
		let streamMeta: StreamMeta | undefined;
		let currentLastEventId = lastEventId;
		let reconnectSucceeded = false;

		const connectAndProcessStream = async () => {
			const { generator, responseData } = await assistanceService.reconnectToMessageStream({
				conversationId,
				messageId,
				lastEventId: currentLastEventId,
				controller: abortController,
			});

			traceId = responseData?.traceId;

			metrics.onReconnectEstablished();

			const { hasReceivedFinalResponse } = await processReconnectStreamMessage({
				generator,
				traceId: responseData?.traceId,
				messageId,
				conversationId,
				answerIndex,
				isStagingArea: chatContextStoreActions.getIsStagingArea(),
				isDeepResearch,
				getState,
				setMessages,
				getMessages,
				processStreamResearch,
				processMessageStreamAction,
				processMessageStreamComplete,
				processMessageStreamActionClassification,
				sendAnalyticsEvent,
				updateCurrentConversation,
				addImplicitResearchPlan,
				resetHasReceivedAnswerPart,
				onStreamEvent: (event, streamMessage, eventId) => {
					// Update currentLastEventId progressively as we process each event
					// so that retries can resume from the last successfully processed event
					if (eventId) {
						currentLastEventId = eventId;
					}

					if (event === 'chunk') {
						metrics.onReconnectChunkReceived();

						if (streamMessage && isStreamResponse(streamMessage)) {
							streamMeta = extractStreamMeta(streamMessage as StreamResponse);
						}
					}
					if (event === 'error') {
						if (isStreamAUPError(streamMessage)) {
							reconnectSucceeded = true;
							metrics.onReconnectSucceeded();
						}
					}
				},
			});

			if (abortController.signal.aborted) {
				throw new DOMException('Reconnect aborted', 'AbortError');
			}

			if (!hasReceivedFinalResponse) {
				throw new NoFinalResponseError(
					'The response took too long and timed out. Try refreshing the page.',
					{
						traceId,
					},
				);
			}
		};

		//** CONNECT TO STREAM (with retry) */
		try {
			metrics.onReconnectStarted();
			updateCurrentConversation({
				status: CHAT_STORE_STATUS.typingResponse,
				traces: [{ messageContent: 'Resuming response...', status: 'loading' }],
				storeAbortController,
			});

			const isReconnectAllEnabled = isReconnectToAll ?? false;

			if (isReconnectAllEnabled) {
				const reconnect = (eventId: string | undefined) =>
					assistanceService.reconnectToMessageStream({
						conversationId,
						messageId,
						lastEventId: eventId,
						controller: abortController,
					});

				// Capture timestamp BEFORE the API call so that any live chunks
				// still in-flight during the round-trip are correctly identified
				// as live rather than replay.
				const reconnectTimestamp = Date.now();

				const { generator: initialGenerator, responseData } = await reconnect(currentLastEventId);

				traceId = responseData?.traceId;
				metrics.onReconnectEstablished();

				const wrappedGenerator = createReconnectableStream<ParsedSSEMessage<StreamMessage>>({
					initialGenerator,
					canReconnect: () => !abortController.signal.aborted,
					getReconnectGenerator: (eventId) => reconnect(eventId).then((r) => r.generator),
					getEventId: (chunk) => (chunk.id ? chunk.id : undefined),
					onReconnecting: (attempt, maxAttempts, eventId) => {
						totalAttempts = attempt;
						sendAnalyticsEvent({
							eventType: 'track',
							action: 'retrying',
							actionSubject: 'rovoChatReconnection',
							attributes: {
								conversationId,
								messageId,
								isDeepResearch,
								attempt,
								maxAttempts,
								hasLastEventId: !!eventId,
							},
						});
						updateCurrentConversation({
							traces: [
								{
									messageContent: `Connection lost. Retrying... (${attempt}/${maxAttempts})`,
									status: 'loading',
								},
							],
						});
					},
					onReconnected: () => {
						metrics.onReconnectEstablished();
					},
					shouldContinueRetrying: () => {
						if (!getIsChatMounted()) {
							abortController.abort(RECONNECT_ABORT_REASON_CHAT_UNMOUNTED);
							return false;
						}
						return true;
					},
					signal: abortController.signal,
				});

				// Batch store updates and chunk-merge REASONING_PART during replay;
				// both flushed together via onFlush at the replay→live boundary.
				const reasoningChunkBuffer = createReasoningChunkBuffer((mergedEvent) => {
					processStreamResearch({
						messageId,
						streamMessage: mergedEvent,
						isDeepResearch: isDeepResearch ?? false,
					});
				});

				const {
					wrapGenerator,
					batchedSetMessages,
					batchedGetMessages,
					batchedProcessStreamResearch,
					batchedUpdateCurrentConversation,
					getIsReplaying,
					getCompletedStepIds,
				} = withReplayFlush({
					conversationId,
					reconnectTimestamp,
					getMessages,
					setMessages,
					processStreamResearch,
					updateCurrentConversation,
					// Use flushFiltered so reasoning chunks for already-completed steps
					// are dropped — this prevents spurious "Rovo is thinking" rounds
					// from appearing in the research expandable after reconnect replay.
					// Steps that are still in-progress (no STEP_COMPLETED seen) are
					// flushed normally so live streaming continues correctly.
					onFlush: () =>
						reasoningChunkBuffer.flushFiltered((stepId) => !getCompletedStepIds().has(stepId)),
				});

				const { hasReceivedFinalResponse } = await processReconnectStreamMessage({
					generator: wrapGenerator(wrappedGenerator),
					traceId: responseData?.traceId,
					messageId,
					conversationId,
					answerIndex,
					isStagingArea: chatContextStoreActions.getIsStagingArea(),
					isDeepResearch,
					getState,
					setMessages: batchedSetMessages,
					getMessages: batchedGetMessages,
					processStreamResearch: batchedProcessStreamResearch,
					// skip batching for Stream Actions for now, since string accumulation is not guarnateed and vary based on the action type.
					// TODO: should be imrpoved in future snapshot event support.
					processMessageStreamAction,
					processMessageStreamComplete,
					processMessageStreamActionClassification,
					sendAnalyticsEvent,
					updateCurrentConversation: batchedUpdateCurrentConversation,
					getIsReplaying,
					reasoningChunkBuffer,
					addImplicitResearchPlan,
					resetHasReceivedAnswerPart,
					onStreamEnd: () => {
						// Mirror onFlush: drop reasoning for completed steps.
						reasoningChunkBuffer.flushFiltered((stepId) => !getCompletedStepIds().has(stepId));
					},
					onTaskStatusDraft: ({ taskId, title, updatedAt, messageId: msgId }) => {
						if (!unifiedTasksActions || !fg('rovo_chat_enable_tasks')) {
							return;
						}
						unifiedTasksActions.createTaskFromStream({
							conversationId,
							taskId,
							title,
							updatedAt,
							messageId: msgId,
						});
					},
					onTaskStatusCompleted: ({ title, updatedAt, completionMessageId }) => {
						if (!unifiedTasksActions || !fg('rovo_chat_enable_tasks')) {
							return;
						}
						unifiedTasksActions.setTaskProgressState({
							conversationId,
							progressState: 'Completed',
							title,
							updatedAt,
							seekingCompletedAck: true,
							completionMessageId,
						});
					},
					onTodoSnapshot: ({ todos }) => {
						if (!unifiedTasksActions || !fg('rovo_chat_enable_tasks')) {
							return;
						}
						unifiedTasksActions.setTaskTodoSnapshot({
							conversationId,
							todoSnapshot: { todos },
						});
					},
					onStreamEvent: (event, streamMessage, eventId) => {
						if (event === 'chunk') {
							metrics.onReconnectChunkReceived();

							if (streamMessage && isStreamResponse(streamMessage)) {
								streamMeta = extractStreamMeta(streamMessage as StreamResponse);
							}
						}
						if (event === 'error') {
							if (isStreamAUPError(streamMessage)) {
								reconnectSucceeded = true;
								metrics.onReconnectSucceeded();
							}
						}
					},
				});

				if (abortController.signal.aborted) {
					throw new DOMException('Reconnect aborted', 'AbortError');
				}

				if (!hasReceivedFinalResponse) {
					throw new NoFinalResponseError(
						'The response took too long and timed out. Try refreshing the page.',
						{
							traceId,
						},
					);
				}
			} else {
				// Legacy path: exponentialRetry wraps the full connect + process cycle
				await exponentialRetry({
					fn: connectAndProcessStream,
					maxRetries: MAX_RECONNECT_ATTEMPTS,
					delay: RETRY_BASE_DELAY_MS,
					postOnlineDelay: POST_ONLINE_DELAY_MS,
					errorFilter: (error) => {
						if (!getIsChatMounted()) {
							abortController.abort(RECONNECT_ABORT_REASON_CHAT_UNMOUNTED);
							return false;
						}
						return isRetryableReconnectionError(error);
					},
					jitter: true,
					signal: abortController.signal,
					pauseWhileOffline: true,
					onRetry: (attempt, _error) => {
						totalAttempts = attempt + 1;
						sendAnalyticsEvent({
							eventType: 'track',
							action: 'retrying',
							actionSubject: 'rovoChatReconnection',
							attributes: {
								conversationId,
								messageId,
								isDeepResearch,
								attempt,
								maxAttempts: MAX_RECONNECT_ATTEMPTS,
								hasLastEventId: !!currentLastEventId,
							},
						});
						updateCurrentConversation({
							traces: [
								{
									messageContent: `Connection lost. Retrying... (${attempt}/${MAX_RECONNECT_ATTEMPTS})`,
									status: 'loading',
								},
							],
						});
					},
				});
			}

			metrics.onReconnectSucceeded();
			onComplete?.({ success: true, streamMeta });
		} catch (error) {
			if (
				(error instanceof Error && error.name === 'AbortError') ||
				abortController.signal.aborted
			) {
				confirmAbort();
				updateCurrentConversation({ lastAgentMessageStatus: 'FAILED' });
				metrics.onReconnectAborted();
				// Only cancel the message stream if the abort reason is not the chat unmounted reason
				if (abortController.signal.reason !== RECONNECT_ABORT_REASON_CHAT_UNMOUNTED) {
					cancelMessageStream();
				}
				return;
			}

			if (!reconnectSucceeded) {
				metrics.onReconnectFailed(error as Error);
			}

			if (isDeepResearch) {
				markIncompleteResearchesErrored({ messageId });
			}

			onComplete?.({
				success: reconnectSucceeded,
				error: error instanceof Error ? error : new Error(String(error)),
			});
			throw error;
		} finally {
			endReconnection(conversationId);
			updateCurrentConversation(clearStatus({}));
			setMessages(conversationId, markStreamingMessagesAsComplete(getMessages(conversationId)));
		}
	};
